import React, { useCallback, useState, useEffect } from 'react';
import { apiUrl } from '../services/api';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import LodgingForm from '../components/LodgingForm';
import { openNativePicker } from '../utils/travelLegs';
import { requireFields, validateDateRange } from '../utils/validation';
import { usePlaceSuggestions } from '../hooks/usePlaceSuggestions';
import { useMenuClose } from '../hooks/useMenuClose';

function TripSetup() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [tripName, setTripName] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [openDestinationMenuId, setOpenDestinationMenuId] = useState(null);
  const [newDestination, setNewDestination] = useState({ destination_name: '', start_date: '', end_date: '' });
  const [newDestinationLodging, setNewDestinationLodging] = useState({ lodging_name: '', lodging_type: '', address: '', lat: null, lon: null, start_date: '', end_date: '' });
  const [isAddingDestination, setIsAddingDestination] = useState(false);
  const [isAddingDestinationLodging, setIsAddingDestinationLodging] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);

  const [activityTypes, setActivityTypes] = useState([]);
  const [activityCategories, setActivityCategories] = useState([]);
  const [editingDestinationId, setEditingDestinationId] = useState(null);
  const [editDestination, setEditDestination] = useState({ destination_name: '', start_date: '', end_date: '' });
  const [editSelectedPlace, setEditSelectedPlace] = useState(null);

  const {
    suggestions: destinationSuggestions,
    activeIndex: activeSuggestionIndex,
    setActiveIndex: setActiveSuggestionIndex,
    clearSuggestions: clearDestinationSuggestions,
  } = usePlaceSuggestions(newDestination.destination_name);

  const {
    suggestions: editDestinationSuggestions,
    activeIndex: editActiveSuggestionIndex,
    setActiveIndex: setEditActiveSuggestionIndex,
    clearSuggestions: clearEditDestinationSuggestions,
  } = usePlaceSuggestions(editDestination.destination_name, { enabled: !!editingDestinationId });

  const fromQuery = (() => {
    try {
      const v = new URLSearchParams(location.search || '').get('from');
      return v && v.startsWith('/') ? v : null;
    } catch {
      return null;
    }
  })();
  const fromState = location.state && typeof location.state.from === 'string' && location.state.from.startsWith('/')
    ? location.state.from
    : null;
  const returnTo = fromQuery || fromState;

  const refetchTypes = useCallback(() => {
    fetch(apiUrl('/api/activity_types'))
      .then(res => res.ok ? res.json() : [])
      .then(setActivityTypes)
      .catch(() => {});
  }, []);

  useEffect(() => {
    refetchTypes();
    fetch(apiUrl('/api/activity_categories'))
      .then(res => res.ok ? res.json() : [])
      .then(setActivityCategories)
      .catch(() => {});

    // Fetch trip name
    fetch(apiUrl('/api/trips'))
      .then(res => res.json())
      .then(trips => {
        const trip = trips.find(t => t.trip_id === tripId);
        if (trip) setTripName(trip.trip_name);
      });

    // Fetch destinations
    fetch(apiUrl(`/api/trips/${tripId}/destinations`))
      .then(res => res.json())
      .then(setDestinations);
  }, [tripId, refetchTypes]);

  useMenuClose(!!openDestinationMenuId, () => setOpenDestinationMenuId(null));

  const selectSuggestion = (s) => {
    setNewDestination(prev => ({ ...prev, destination_name: s.label || s.display_name || prev.destination_name }));
    setSelectedPlace(s);
    clearDestinationSuggestions();
  };

  const selectEditSuggestion = (s) => {
    setEditDestination(prev => ({ ...prev, destination_name: s.label || s.display_name || prev.destination_name }));
    setEditSelectedPlace(s);
    clearEditDestinationSuggestions();
  };

  const addDestination = () => {
    if (!requireFields([
      [newDestination.destination_name, 'Destination Name'],
      [newDestination.start_date, 'Start Date'],
      [newDestination.end_date, 'End Date'],
    ])) return;
    if (!validateDateRange(newDestination.start_date, newDestination.end_date)) return;
    const payload = {
      ...newDestination,
      place: selectedPlace || undefined,
    };

    const anyLodging = Object.values(newDestinationLodging || {}).some(v => String(v || '').trim());
    if (anyLodging) {
      const missingL = [];
      if (!String(newDestinationLodging.lodging_name || '').trim()) missingL.push('Lodging Name');
      if (!String(newDestinationLodging.lodging_type || '').trim()) missingL.push('Lodging Type');
      if (!String(newDestinationLodging.address || '').trim()) missingL.push('Address');
      if (!newDestinationLodging.start_date) missingL.push('Lodging Start Date');
      if (!newDestinationLodging.end_date) missingL.push('Lodging End Date');
      if (missingL.length) {
        window.alert(`Please provide:\n- ${missingL.join('\n- ')}`);
        return;
      }
      if (newDestinationLodging.end_date < newDestinationLodging.start_date) {
        window.alert('Lodging End Date must be on or after Start Date.');
        return;
      }

      payload.lodging = {
        lodging_name: String(newDestinationLodging.lodging_name || '').trim(),
        lodging_type: String(newDestinationLodging.lodging_type || '').trim(),
        address: String(newDestinationLodging.address || '').trim(),
        lat: newDestinationLodging.lat,
        lon: newDestinationLodging.lon,
        start_date: newDestinationLodging.start_date,
        end_date: newDestinationLodging.end_date,
        check_in_time: String(newDestinationLodging.check_in_time || '').trim() || null,
        check_out_time: String(newDestinationLodging.check_out_time || '').trim() || null,
        booking_url: String(newDestinationLodging.booking_url || '').trim() || null,
        booking_image_url: String(newDestinationLodging.booking_image_url || '').trim() || null,
      };
    }

    fetch(apiUrl(`/api/trips/${tripId}/destinations`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => res.json())
      .then(() => {
        setNewDestination({ destination_name: '', start_date: '', end_date: '' });
        setNewDestinationLodging({ lodging_name: '', lodging_type: '', address: '', lat: null, lon: null, start_date: '', end_date: '' });
        setIsAddingDestination(false);
        setIsAddingDestinationLodging(false);
        setSelectedPlace(null);
        clearDestinationSuggestions();
        // Refetch
        fetch(apiUrl(`/api/trips/${tripId}/destinations`))
          .then(res => res.json())
          .then(setDestinations);
      });
  };

  const startEditingDestination = (destination) => {
    setEditingDestinationId(destination.destination_id);
    setEditDestination({
      destination_name: destination.destination_name || '',
      start_date: destination.start_date || '',
      end_date: destination.end_date || '',
    });
    setEditSelectedPlace(null);
    clearEditDestinationSuggestions();
  };

  const cancelEditingDestination = () => {
    setEditingDestinationId(null);
    setEditSelectedPlace(null);
    clearEditDestinationSuggestions();
  };

  const saveDestination = async (destinationId) => {
    if (!requireFields([
      [editDestination.destination_name, 'Destination Name'],
      [editDestination.start_date, 'Start Date'],
      [editDestination.end_date, 'End Date'],
    ])) return;
    if (!validateDateRange(editDestination.start_date, editDestination.end_date)) return;

    const payload = {
      ...editDestination,
      place: editSelectedPlace || undefined,
    };

    const res = await fetch(apiUrl(`/api/trips/${tripId}/destinations/${destinationId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const out = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(out.error || 'Failed to update destination');
      return;
    }

    cancelEditingDestination();
    fetch(apiUrl(`/api/trips/${tripId}/destinations`))
      .then(r => r.json())
      .then(setDestinations);
  };

  const removeDestination = (destinationId) => {
    fetch(apiUrl(`/api/trips/${tripId}/destinations/${destinationId}`), {
      method: 'DELETE'
    })
      .then(() => {
        setDestinations(destinations.filter(d => d.destination_id !== destinationId));
      });
  };

  return (
    <div>
      <section className="tp-hero">
        <p className="tp-hero__kicker">Trip Setup</p>
        <h1 className="tp-hero__title">{tripName || 'Trip'}</h1>
        <p className="tp-hero__subtitle">Add destinations and date ranges for this trip.</p>
        <div className="tp-row" style={{ marginTop: 14 }}>
          <button className="tp-btn--ghost" onClick={() => navigate('/')}>Back to Home</button>
          {returnTo && (
            <button className="tp-btn--ghost" onClick={() => navigate(returnTo)}>Back to Itinerary</button>
          )}
          <button className="tp-btn--ghost" onClick={() => navigate(`/trips/${tripId}/research`)}>Research</button>
          <button onClick={() => navigate(`/itinerary/${tripId}`)}>Go to Itinerary</button>
        </div>
      </section>

      <section className="tp-section">
        <div className="tp-card">
          <div className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="tp-section__title" style={{ margin: 0 }}>Destinations</h2>
            <button
              type="button"
              className="tp-btn--icon"
              aria-label="Add destination"
              onClick={() => setIsAddingDestination(true)}
            >
              +
            </button>
          </div>

          {destinations.length === 0 ? (
            <p style={{ color: 'var(--tp-muted)', margin: '12px 0 0 0' }}>No destinations yet.</p>
          ) : (
            <table className="tp-table" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Start</th>
                  <th>End</th>
                  <th className="tp-th--tight"></th>
                </tr>
              </thead>
              <tbody>
                {destinations.map(destination => (
                  <tr key={destination.destination_id}>
                    <td style={{ width: '48%' }}>
                      {String(editingDestinationId) === String(destination.destination_id) ? (
                        <div>
                          <input
                            type="text"
                            value={editDestination.destination_name}
                            onChange={e => {
                              setEditDestination(prev => ({ ...prev, destination_name: e.target.value }));
                              setEditSelectedPlace(null);
                            }}
                            onKeyDown={e => {
                              if (!editDestinationSuggestions.length) return;
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setEditActiveSuggestionIndex(i => (i + 1) % editDestinationSuggestions.length);
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                setEditActiveSuggestionIndex(i => (i <= 0 ? editDestinationSuggestions.length - 1 : i - 1));
                              } else if (e.key === 'Enter') {
                                e.preventDefault();
                                const idx = editActiveSuggestionIndex >= 0 ? editActiveSuggestionIndex : 0;
                                selectEditSuggestion(editDestinationSuggestions[idx]);
                              } else if (e.key === 'Escape') {
                                clearEditDestinationSuggestions();
                              }
                            }}
                          />
                          {editDestinationSuggestions.length > 0 && (
                            <div className="tp-card tp-card--tight" style={{ marginTop: 10 }}>
                              {editDestinationSuggestions.map((s, idx) => (
                                <div
                                  key={`${s.osm_type || 'osm'}-${s.osm_id || idx}`}
                                  onMouseDown={() => selectEditSuggestion(s)}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderTop: idx === 0 ? 'none' : '1px solid var(--tp-border)',
                                    background: idx === editActiveSuggestionIndex ? 'var(--tp-surface-2)' : 'transparent'
                                  }}
                                >
                                  <div style={{ fontSize: 14 }}>{s.label || s.display_name}</div>
                                  {!!s.subtitle && (
                                    <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 2 }}>{s.subtitle}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {destination.destination_name}
                        </div>
                      )}
                    </td>
                    <td>
                      {String(editingDestinationId) === String(destination.destination_id) ? (
                        <input
                          type="date"
                          value={editDestination.start_date}
                          onClick={openNativePicker}
                          onChange={e => {
                            const v = e.target.value;
                            setEditDestination(prev => ({
                              ...prev,
                              start_date: v,
                              end_date: prev.end_date && v && prev.end_date < v ? '' : prev.end_date,
                            }));
                          }}
                        />
                      ) : (
                        destination.start_date
                      )}
                    </td>
                    <td>
                      {String(editingDestinationId) === String(destination.destination_id) ? (
                        <input
                          type="date"
                          min={editDestination.start_date || ''}
                          value={editDestination.end_date}
                          onClick={openNativePicker}
                          onChange={e => {
                            const v = e.target.value;
                            setEditDestination(prev => {
                              const start = prev.start_date;
                              if (start && v && v < start) return prev;
                              return { ...prev, end_date: v };
                            });
                          }}
                        />
                      ) : (
                        destination.end_date
                      )}
                    </td>

                    <td className="tp-td--tight">
                      {String(editingDestinationId) === String(destination.destination_id) ? (
                        <div className="tp-row" style={{ justifyContent: 'flex-end' }}>
                          <button onClick={() => saveDestination(destination.destination_id)}>Save</button>
                          <button className="tp-btn--ghost" onClick={cancelEditingDestination}>Cancel</button>
                        </div>
                      ) : (
                        <div className="tp-actions">
                          <button
                            type="button"
                            className="tp-btn--icon tp-btn--actions"
                            aria-label="Destination actions"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDestinationMenuId((cur) => (String(cur) === String(destination.destination_id) ? null : destination.destination_id));
                            }}
                          >
                            ⋮
                          </button>
                          {String(openDestinationMenuId) === String(destination.destination_id) && (
                            <div
                              className="tp-card tp-card--tight tp-menu"
                              style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 60, minWidth: 160 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="tp-btn--ghost"
                                onClick={() => {
                                  setOpenDestinationMenuId(null);
                                  startEditingDestination(destination);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="tp-btn--danger"
                                style={{ marginTop: 8 }}
                                onClick={() => {
                                  setOpenDestinationMenuId(null);
                                  if (!window.confirm(`Remove ${destination.destination_name}?`)) return;
                                  removeDestination(destination.destination_id);
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!isAddingDestination ? null : (
            <div style={{ marginTop: 16 }}>
              <div className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="tp-section__title" style={{ margin: 0 }}>Add Destination</h3>
                <button
                  className="tp-btn--ghost"
                  onClick={() => {
                    setIsAddingDestination(false);
                    setIsAddingDestinationLodging(false);
                    setNewDestination({ destination_name: '', start_date: '', end_date: '' });
                    setNewDestinationLodging({ lodging_name: '', lodging_type: '', address: '', lat: null, lon: null, start_date: '', end_date: '' });
                    setSelectedPlace(null);
                    clearDestinationSuggestions();
                  }}
                >
                  Cancel
                </button>
              </div>
              <div className="tp-field">
                <span className="tp-label">Destination Name</span>
                <input
                  type="text"
                  placeholder="e.g., Amsterdam"
                  required
                  value={newDestination.destination_name}
                  onChange={e => {
                    setNewDestination({ ...newDestination, destination_name: e.target.value });
                    setSelectedPlace(null);
                  }}
                  onKeyDown={e => {
                    if (!destinationSuggestions.length) return;
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setActiveSuggestionIndex(i => (i + 1) % destinationSuggestions.length);
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setActiveSuggestionIndex(i => (i <= 0 ? destinationSuggestions.length - 1 : i - 1));
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      const idx = activeSuggestionIndex >= 0 ? activeSuggestionIndex : 0;
                      selectSuggestion(destinationSuggestions[idx]);
                    } else if (e.key === 'Escape') {
                      clearDestinationSuggestions();
                    }
                  }}
                />
                {destinationSuggestions.length > 0 && (
                  <div className="tp-card tp-card--tight" style={{ marginTop: 10 }}>
                    {destinationSuggestions.map((s, idx) => (
                      <div
                        key={`${s.osm_type || 'osm'}-${s.osm_id || idx}`}
                        onMouseDown={() => selectSuggestion(s)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderTop: idx === 0 ? 'none' : '1px solid var(--tp-border)',
                          background: idx === activeSuggestionIndex ? 'var(--tp-surface-2)' : 'transparent'
                        }}
                      >
                        <div style={{ fontSize: 14 }}>{s.label || s.display_name}</div>
                        {!!s.subtitle && (
                          <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 2 }}>{s.subtitle}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="tp-grid" style={{ gap: 12 }}>
                <div className="tp-col-6">
                  <div className="tp-field">
                    <span className="tp-label">Start Date</span>
                    <input
                      type="date"
                      required
                      value={newDestination.start_date}
                      onClick={openNativePicker}
                      onChange={e => {
                        const v = e.target.value;
                        setNewDestination(prev => ({
                          ...prev,
                          start_date: v,
                          end_date: prev.end_date && v && prev.end_date < v ? '' : prev.end_date,
                        }));
                      }}
                    />
                  </div>
                </div>
                <div className="tp-col-6">
                  <div className="tp-field">
                    <span className="tp-label">End Date</span>
                    <input
                      type="date"
                      required
                      min={newDestination.start_date || ''}
                      value={newDestination.end_date}
                      onClick={openNativePicker}
                      onChange={e => {
                        const v = e.target.value;
                        setNewDestination(prev => {
                          const start = prev.start_date;
                          if (start && v && v < start) return prev;
                          return { ...prev, end_date: v };
                        });
                      }}
                    />
                  </div>
                </div>
              </div>

              {!isAddingDestinationLodging ? (
                <div className="tp-row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
                  <span className="tp-pill">Lodging (optional)</span>
                  <button className="tp-btn--ghost" onClick={() => setIsAddingDestinationLodging(true)}>Add Lodging</button>
                </div>
              ) : (
                <div className="tp-card tp-card--tight" style={{ marginTop: 12 }}>
                  <div className="tp-row" style={{ justifyContent: 'space-between' }}>
                    <h3 className="tp-section__title" style={{ margin: 0, fontSize: 14 }}>Lodging</h3>
                    <button className="tp-btn--ghost" onClick={() => {
                      setIsAddingDestinationLodging(false);
                      setNewDestinationLodging({ lodging_name: '', lodging_type: '', address: '', lat: null, lon: null, start_date: '', end_date: '' });
                    }}>Cancel</button>
                  </div>
                  {(() => {
                    const lodgingCat = (activityCategories || []).find(c => String(c?.category_name || '').toLowerCase() === 'lodging');
                    const lodgingCatId = lodgingCat ? String(lodgingCat.category_id) : '';
                    return (
                      <LodgingForm
                        draft={newDestinationLodging}
                        setDraft={setNewDestinationLodging}
                        activityTypes={activityTypes}
                        lodgingCategoryId={lodgingCatId}
                        onTypeCreated={refetchTypes}
                        openNativePicker={openNativePicker}
                      />
                    );
                  })()}
                </div>
              )}

              <div className="tp-row">
                <button onClick={addDestination}>Add Destination</button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default TripSetup;